"use client";

import { c as _c } from "react/compiler-runtime";
import { type ChartConfig, ChartContainer, ChartTooltip, ChartTooltipContent } from "@shared-components/shadcn/chart";
import { api } from "@shared-server/api";
import { Bar, ComposedChart, CartesianGrid, Line, XAxis, YAxis, Legend } from "recharts";
import { formatGranularDateTick } from "./metric-utils";
import type { GranularityType } from "@shared-server/api/routers/queries/metrics/utils";
import { useDepartmentStackContext } from "@shared-components/department-stack-provider";
const chartConfig: ChartConfig = {
  costDollars: {
    label: "Cost ($)",
    color: "#006e96"
  },
  costCumulativeDollars: {
    label: "Cumulative Cost ($)",
    color: "#eaaa00"
  }
};
type ResearchCostProps = {
  startDate: Date;
  endDate: Date;
  granularity: GranularityType;
};
export const ResearchCost = props => {
  const $ = _c(17);
  const {
    parentDepartmentId
  } = useDepartmentStackContext();
  let t0;
  if ($[0] !== parentDepartmentId || $[1] !== props.endDate || $[2] !== props.granularity || $[3] !== props.startDate) {
    t0 = {
      startDate: props.startDate,
      endDate: props.endDate,
      granularity: props.granularity,
      parentDepartmentId
    };
    $[0] = parentDepartmentId;
    $[1] = props.endDate;
    $[2] = props.granularity;
    $[3] = props.startDate;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  const researchCostQuery = api.coreMetrics.researchCostPerDay.useQuery(t0);
  let t1;
  if ($[5] !== researchCostQuery.data?.chartData) {
    t1 = researchCostQuery.data?.chartData ?? [];
    $[5] = researchCostQuery.data?.chartData;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const chartData = t1;
  const totalCostDollars = researchCostQuery.data?.totalCostDollars ?? 0;
  let t2;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <h2 className="mb-4">{"Research Cost"}</h2>;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== chartData.length) {
    t3 = chartData.length === 0 && <div className="text-gray-500">{"No data available for selected range."}</div>;
    $[8] = chartData.length;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== chartData || $[11] !== props.granularity || $[12] !== totalCostDollars) {
    t4 = chartData.length !== 0 && <><p className="mb-4 text-sm text-muted-foreground">{"Total Cost: $"}{totalCostDollars.toFixed(2)}</p><ChartContainer config={chartConfig} className="aspect-auto h-[200px] w-full"><ComposedChart accessibilityLayer={true} data={chartData}><CartesianGrid vertical={false} /><XAxis dataKey="date" tickLine={false} axisLine={false} tickMargin={8} minTickGap={32} tickFormatter={value => formatGranularDateTick(value, props.granularity)} /><YAxis yAxisId="costDollars" dataKey="costDollars" orientation="left" tickFormatter={_temp} /><YAxis yAxisId="costCumulativeDollars" dataKey="costCumulativeDollars" orientation="right" tickFormatter={_temp2} /><ChartTooltip content={<ChartTooltipContent nameKey="date" labelFormatter={_temp3} formatter={_temp4} />} /><Legend /><Bar yAxisId="costDollars" dataKey="costDollars" name="Daily Cost ($)" fill="#006e96" /><Line yAxisId="costCumulativeDollars" dataKey="costCumulativeDollars" name="Cumulative Cost ($)" dot={false} stroke="#eaaa00" /></ComposedChart></ChartContainer></>;
    $[10] = chartData;
    $[11] = props.granularity;
    $[12] = totalCostDollars;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== t3 || $[15] !== t4) {
    t5 = <div>{t2}{t3}{t4}</div>;
    $[14] = t3;
    $[15] = t4;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  return t5;
};
function _temp(value_0) {
  return `$${value_0.toFixed(2)}`;
}
function _temp2(value_1) {
  return `$${value_1.toFixed(2)}`;
}
function _temp3(value_2) {
  return value_2;
}
function _temp4(value_3, name) {
  if (typeof value_3 === "number") {
    return [`$${value_3.toFixed(2)}`, name];
  }
  return [value_3, name];
}